import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SessionStorage keys for the event create flow.
 * Drafted event id is stored under CREATE_SHOW_DRAFT_KEY ('marq_create_show_id');
 * it is set when a draft is created (e.g. ai-draft, choose-type) and read in
 * onboarding and event steps to assign the draft to the manager after signup.
 */
export const CREATE_SHOW_DRAFT_KEY = 'marq_create_show_id';
export const AI_PROMPT_STORAGE_KEY = 'marq_ai_prompt';
export const AI_PROMPT_OPTS_STORAGE_KEY = 'marq_ai_prompt_opts';
export const FREE_EVENT_OPTIONS_STORAGE_PREFIX = 'marq_free_event_options_';

/**
 * Paths where we preserve draft/session (event create flow + unprotected auth routes).
 * Do NOT clear on login, register, verify-otp, verify-reset, or onboarding.
 * All onboarding steps (e.g. /onboarding, /onboarding?step=2) keep the draft id
 * so the drafted event can be reassigned to the new user after setup. Clear only
 * when user enters protected app routes (dashboard, payouts, events, settings, etc.).
 */
const EVENT_CREATE_PATHS = [
  '/ai-generated',
  '/ai-draft',
  '/ai-prompt',
  '/draft',
  '/choose-type',
  '/event-basics',
  '/date-and-time',
  '/ticket-and-pricing',
  '/event-seat-location',
  '/media-and-branding',
  '/media',
  '/payment-methods',
  '/raffle-configuration',
  '/food-and-beverages',
  '/attendee-registration',
  '/terms-and-settings',
  '/terms-and-notes',
  '/terms-preview',
  '/additional-details',
  '/event-preview',
  '/date-and-location',
  '/form-builder',
  '/add-vendors',
  '/build-schedule',
  '/onboarding',
  '/register',
  '/login',
  '/verify-otp',
  '/verify-reset',
];

const ONBOARDING_STEP_KEY = 'marq_onboarding_step';
const FROM_AI_DRAFT_KEY = 'marq_from_ai_draft';
const MARQ_RETURN_PAGE_COOKIE = 'marq_return_page';
const MARQ_CREATE_SHOW_ID_COOKIE = 'marq_create_show_id';

/**
 * Clears event-create-related sessionStorage so the next "create event" flow starts fresh.
 * Call when user navigates away from the event create flow (e.g. dashboard, events, settings).
 */
export const clearEventCreateStorage = () => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(CREATE_SHOW_DRAFT_KEY);
    sessionStorage.removeItem(AI_PROMPT_STORAGE_KEY);
    sessionStorage.removeItem(AI_PROMPT_OPTS_STORAGE_KEY);
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(FREE_EVENT_OPTIONS_STORAGE_PREFIX)) {
        sessionStorage.removeItem(k);
      }
    }
  } catch {
    // ignore
  }
};

/**
 * Clears all step-related sessionStorage and cookies (onboarding step, event draft id,
 * AI prompt, return page). Call on logout so the next session does not restore old steps.
 * @param {Function} removeCookiesFn - Optional async function (key) => Promise to remove a cookie
 */
export const clearStepStorageOnLogout = async (removeCookiesFn) => {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(ONBOARDING_STEP_KEY);
      sessionStorage.removeItem(FROM_AI_DRAFT_KEY);
      sessionStorage.removeItem(CREATE_SHOW_DRAFT_KEY);
      sessionStorage.removeItem(AI_PROMPT_STORAGE_KEY);
      sessionStorage.removeItem(AI_PROMPT_OPTS_STORAGE_KEY);
    } catch {
      // ignore
    }
    try {
      localStorage.removeItem(ONBOARDING_STEP_KEY);
    } catch {
      // ignore
    }
  }
  if (typeof removeCookiesFn === 'function') {
    await removeCookiesFn(MARQ_RETURN_PAGE_COOKIE);
    await removeCookiesFn(MARQ_CREATE_SHOW_ID_COOKIE);
  }
};

const isEventCreatePath = (pathname) =>
  EVENT_CREATE_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Clears event-create sessionStorage (draft id + AI prompt) only when the user
 * navigates to protected app routes (dashboard, payouts, events, settings, etc.).
 * Does NOT clear on login, register, verify-otp, verify-reset, or any onboarding
 * step—so the draft id is preserved and the drafted event can be reassigned to
 * the newcomer after onboarding.
 */
export function EventCreateStorageCleanup() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!isEventCreatePath(pathname)) {
      clearEventCreateStorage();
    }
  }, [pathname]);

  return null;
}
